<script lang="ts" setup>
import { computed, ref } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import {
  ButtonSize,
  TuButton,
  TuIconButton,
  TuPopover,
  TuSocialShareDropdown,
  TuTooltipWrapper,
  usePopover,
  type AvailableNetworks,
} from "@tinyurldev/tu-ui-vue";

import useTranslation from "@Composables/useTranslation";

interface Props {
  url?: string;
  networks?: AvailableNetworks[];
}

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);

//
// Props
withDefaults(defineProps<Props>(), {
  url: "",
  networks: () => ["facebook", "twitter", "whatsapp", "linkedin", "email"],
});

//
// Data
const isPopoverVisible = ref(false);
const { referenceRef, popoverRef } = usePopover(isPopoverVisible, { placement: "bottom" });

//
// Computed
const isMobileView = breakpoints.smallerOrEqual("xl");

const tooltip = computed(() => {
  if (isPopoverVisible.value) {
    return;
  }

  return __("organization_dashboard.alias_toolbar.share_url_social");
});

//
// Methods
function onShareClick(): void {
  isPopoverVisible.value = !isPopoverVisible.value;
}
</script>

<template>
  <div class="v2-share-dropdown">
    <TuTooltipWrapper :tooltip="tooltip">
      <TuButton
        v-if="!isMobileView"
        ref="referenceRef"
        class="_share"
        :label="__('organization_dashboard.alias_toolbar.share')"
        :aria-label="__('organization_dashboard.alias_toolbar.share_url_social')"
        :title="__('organization_dashboard.alias_toolbar.share_url_social')"
        icon-class="fa-solid fa-share-nodes"
        :size="ButtonSize.MEDIUM"
        @click="onShareClick"
      />
      <TuIconButton
        v-else
        ref="referenceRef"
        class="_share"
        :aria-label="__('organization_dashboard.alias_toolbar.share_url_social')"
        :title="__('organization_dashboard.alias_toolbar.share_url_social')"
        icon-class="fa-solid fa-share-nodes"
        @click="onShareClick"
      />
    </TuTooltipWrapper>

    <TuPopover
      v-show="isPopoverVisible"
      ref="popoverRef"
      class="_popover-wrapper _share_popover"
    >
      <TuSocialShareDropdown
        class="v2-share-dropdown__socials _share_options"
        :url="url"
        :networks="networks"
      />
    </TuPopover>
  </div>
</template>

<style lang="scss" scoped>
.v2-share-dropdown {
  &__socials {
    padding: 0;
  }
}
</style>
